import MenuBar from '@components/Tiptap/MenuBar';
import { default as HardBreak } from '@tiptap/extension-hard-break';
import { default as Link } from '@tiptap/extension-link';
import type { JSONContent, UseEditorOptions } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { StarterKit } from '@tiptap/starter-kit';
import buildClassName from '@utils/build-class-name';
import type { InputStatus } from 'antd/es/_util/statusUtils';
import { getMergedStatus, getStatusClassNames } from 'antd/es/_util/statusUtils';
import type { Variant } from 'antd/es/config-provider/context';
import { useComponentConfig } from 'antd/es/config-provider/context';
import DisabledContext from 'antd/es/config-provider/DisabledContext';
import useCSSVarCls from 'antd/es/config-provider/hooks/useCSSVarCls';
import { FormItemInputContext } from 'antd/es/form/context';
import useVariant from 'antd/es/form/hooks/useVariants';
import { useSharedStyle } from 'antd/es/input/style';
import type { CSSProperties, FC } from 'react';
import { useContext, useEffect, useMemo } from 'react';

import useStyle from './style';
import editorStyles from './Tiptap.module.scss';

interface CommonInputProps {
  classes?: {
    affixWrapper?: string;
  };
  classNames?: {
    affixWrapper?: string;
    prefix?: string;
    suffix?: string;
    variant?: string;
  };
  styles?: {
    affixWrapper?: CSSProperties;
    prefix?: CSSProperties;
    suffix?: CSSProperties;
  };
}

interface OwnProps {
  disabled?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  value?: string | readonly string[] | JSONContent | JSONContent[] | null;
  onChange?: (value: string) => void;
  serializeHtml?: (html: string) => string;
  // onChange?: ChangeEventHandler<HTMLDivElement>;
  prefixCls?: string;
  className?: string;
  style?: CSSProperties;
  classNames?: CommonInputProps['classNames'] & {
    textarea?: string;
  };
  styles?: {
    textarea?: CSSProperties;
  };
  rootClassName?: string;
  status?: InputStatus;
  variant?: Variant;
}

const serializeMenuHtml = (html: string) => html.replace(/<p>(.*?)<\/p>/gi, '$1<br>').replace(/<br\s*\/?>$/i, '');

const Tiptap: FC<OwnProps> = (props) => {
  const {
    prefixCls: customizePrefixCls,
    disabled: customDisabled,
    status: customStatus,
    variant: customVariant,
    classNames: classes,
    readOnly: customReadOnly,
    rootClassName,
    className,
    style,
    onChange,
    serializeHtml,
    value,
    ...rest
  } = props;

  const {
    getPrefixCls,
    direction,
    className: contextClassName,
    style: contextStyle,
    classNames: contextClassNames,
  } = useComponentConfig('textArea');

  // =================== Disabled ===================
  const disabled = useContext(DisabledContext);
  const mergedDisabled = (customDisabled ?? disabled) || customReadOnly;

  // ==================== Status ====================
  // TODO: hasFeedback, feedbackIcon - add validation feedback icons
  const { status: contextStatus } = useContext(FormItemInputContext);
  const mergedStatus = getMergedStatus(contextStatus, customStatus);

  const prefixCls = getPrefixCls('input', customizePrefixCls);

  // ==================== Style =====================
  const rootCls = useCSSVarCls(prefixCls);
  const [wrapSharedCSSVar, hashId, cssVarCls] = useSharedStyle(prefixCls, rootClassName);
  const [wrapCSSVar] = useStyle(prefixCls, rootCls);

  const [variant, enableVariantCls] = useVariant('textArea', customVariant);
  const extensions: UseEditorOptions['extensions'] = useMemo(
    () => [
      StarterKit.configure({
        bulletList: false,
        heading: false,
        horizontalRule: false,
        listItem: false,
        orderedList: false,
        link: false,
        hardBreak: false,
      }),
      Link.configure({
        openOnClick: false,
      }),
      HardBreak.extend({
        // Bind the Enter key to the hard break command
        addKeyboardShortcuts() {
          return {
            Enter: () => {
              if (this.editor.isActive('orderedList') || this.editor.isActive('bulletList')) {
                return this.editor.chain().createParagraphNear().run();
              }
              return this.editor.commands.setHardBreak();
            },
          };
        },
      }),
    ],
    [],
  );

  const sanitizeHtml = serializeHtml ?? serializeMenuHtml;

  const editor = useEditor({
    editable: !mergedDisabled,
    editorProps: {
      attributes: {
        class: buildClassName(
          prefixCls,
          hashId && hashId,
          classes?.textarea,
          contextClassNames.textarea,
          editorStyles.editor,
        ),
        // style: { ...contextStyles.textarea! },
      },
    },
    content: value,
    extensions: extensions,
    onUpdate: onChange
      ? ({ editor }) => {
          const html = sanitizeHtml(editor.getHTML());
          onChange(html);
        }
      : undefined,
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== sanitizeHtml(editor.getHTML())) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor, sanitizeHtml]);

  useEffect(() => {
    editor?.setEditable(!mergedDisabled, false);
  }, [editor, mergedDisabled]);

  return (
    <>
      <BubbleMenu
        editor={editor}
        shouldShow={({ editor: currentEditor }) => {
          return (
            !mergedDisabled &&
            (!currentEditor.state.selection.empty ||
              (currentEditor.state.selection.$from.pos !== 1 && currentEditor.state.selection.$from.marks().length > 0))
          );
        }}
      >
        <MenuBar editor={editor} />
      </BubbleMenu>
      {wrapSharedCSSVar(
        wrapCSSVar(
          <EditorContent
            {...rest}
            editor={editor}
            style={{ ...contextStyle, ...style }}
            disabled={mergedDisabled}
            className={buildClassName(
              cssVarCls,
              rootCls,
              className,
              rootClassName,
              contextClassName,
              `${prefixCls}-affix-wrapper`,
              `${prefixCls}-textarea-affix-wrapper`,
              direction === 'rtl' && `${prefixCls}-affix-wrapper-rtl`,
              enableVariantCls && `${prefixCls}-${variant}`,
              getStatusClassNames(prefixCls, mergedStatus),
              hashId && hashId,
            )}
          />,
        ),
      )}
    </>
  );
};

export default Tiptap;
